import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card } from "views/components/ui/Card";
import {
    addCategory,
    deleteCategory,
    updateCategory,
    fetchSubCategoryDetails,
} from "actions/category";
import { toast } from "sonner";
import { Eye, Edit2, X } from "lucide-react";

const CategoryManagement = () => {
    const dispatch = useDispatch();
    const categories = useSelector((state) => state.category.categories);
    const subCategories = useSelector((state) => state.category.subCategories);
    const subCategoryDetails = useSelector(
        (state) => state.category.subCategoryDetails
    );
    const [isAddingCategory, setIsAddingCategory] = useState(false);
    const [isAddingSubCategory, setIsAddingSubCategory] = useState(false);
    const [isViewingSubCategory, setIsViewingSubCategory] = useState(false);
    const [isUpdatingSubCategory, setIsUpdatingSubCategory] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubCategory, setSelectedSubCategory] = useState(null);
    const [updatingSubCategory, setUpdatingSubCategory] = useState(null);
    const [viewingSubCategory, setViewingSubCategory] = useState(null);
    const [newCategory, setNewCategory] = useState({
        category: "",
        subCategory: "",
        description: "",
        insights: "",
        features: "",
        useCases: "",
        advantages: "",
        faq: "",
        icon: "",
        order: 0,
    });

    const handleAddCategory = async (e) => {
        e.preventDefault();
        if (!newCategory.category) {
            toast.error("Category name is required");
            return;
        }

        try {
            await dispatch(addCategory(newCategory));
            setNewCategory({
                category: "",
                subCategory: "",
                description: "",
                icon: "",
                order: 0,
            });
            setIsAddingCategory(false);
        } catch (error) {
            console.error("Error adding category:", error);
        }
    };

    const handleAddSubCategory = async (e) => {
        e.preventDefault();
        if (!selectedCategory || !newCategory.subCategory) {
            toast.error("Category and subcategory name are required");
            return;
        }

        try {
            // Convert line-separated strings to arrays
            const categoryData = {
                ...newCategory,
                category: selectedCategory,
                features: newCategory.features
                    ? newCategory.features
                          .split("\n")
                          .map((f) => f.trim())
                          .filter((f) => f)
                    : [],
                useCases: newCategory.useCases
                    ? newCategory.useCases
                          .split("\n")
                          .map((u) => u.trim())
                          .filter((u) => u)
                    : [],
                advantages: newCategory.advantages
                    ? newCategory.advantages
                          .split("\n")
                          .map((a) => a.trim())
                          .filter((a) => a)
                    : [],
                faq: newCategory.faq
                    ? newCategory.faq
                          .split("\n")
                          .map((f) => f.trim())
                          .filter((f) => f)
                    : [],
            };

            await dispatch(addCategory(categoryData));
            setNewCategory({
                category: "",
                subCategory: "",
                description: "",
                insights: "",
                features: "",
                useCases: "",
                advantages: "",
                faq: "",
                icon: "",
                order: 0,
            });
            setIsAddingSubCategory(false);
        } catch (error) {
            console.error("Error adding subcategory:", error);
        }
    };

    const handleViewSubCategory = async (category, subCategory) => {
        try {
            await dispatch(fetchSubCategoryDetails(category, subCategory));
            setViewingSubCategory({ category, subCategory });
            setIsViewingSubCategory(true);
        } catch (error) {
            console.error("Error fetching subcategory details:", error);
            toast.error("Failed to fetch subcategory details");
        }
    };

    const handleUpdateSubCategory = async (category, subCategory) => {
        try {
            await dispatch(fetchSubCategoryDetails(category, subCategory));
            const subCategoryData = subCategories.find(
                (sc) =>
                    sc.category === category && sc.subCategory === subCategory
            );
            if (subCategoryData) {
                setUpdatingSubCategory({
                    category,
                    subCategory,
                    description: subCategoryData.description || "",
                    insights: subCategoryData.insights || [],
                    features: subCategoryData.features || [],
                    useCases: subCategoryData.useCases || [],
                    advantages: subCategoryData.advantages || [],
                    faq: subCategoryData.faq || [],
                });
            }
        } catch (error) {
            console.error("Error fetching subcategory details:", error);
            toast.error("Failed to fetch subcategory details");
        }
    };

    const handleEditSubCategory = async (category, subCategory) => {
        try {
            const response = await dispatch(
                fetchSubCategoryDetails(category, subCategory)
            );
            setSelectedCategory(category);
            setSelectedSubCategory(subCategory);

            // Use the response data directly instead of subCategoryDetails from store
            if (response) {
                setNewCategory({
                    ...newCategory,
                    category,
                    subCategory,
                    description: response.description || "",
                    insights: Array.isArray(response.insights)
                        ? response.insights.join("\n")
                        : response.insights || "",
                    features: Array.isArray(response.features)
                        ? response.features.join("\n")
                        : "",
                    useCases: Array.isArray(response.useCases)
                        ? response.useCases.join("\n")
                        : "",
                    advantages: Array.isArray(response.advantages)
                        ? response.advantages.join("\n")
                        : "",
                    faq: Array.isArray(response.faq)
                        ? response.faq
                              .map((item) =>
                                  typeof item === "object"
                                      ? `${item.question}\n${item.answer}`
                                      : item
                              )
                              .join("\n\n")
                        : "",
                    order: response.order || 0,
                });
                setIsUpdatingSubCategory(true);
            }
        } catch (error) {
            console.error("Error fetching subcategory details:", error);
            toast.error("Failed to fetch subcategory details");
        }
    };

    const handleDeleteSubCategory = async (categoryName, subCategoryName) => {
        if (
            window.confirm("Are you sure you want to delete this subcategory?")
        ) {
            try {
                await dispatch(
                    deleteCategory(`${categoryName}/${subCategoryName}`)
                );
            } catch (error) {
                console.error("Error deleting subcategory:", error);
            }
        }
    };

    const handleSubmitUpdate = async (e) => {
        e.preventDefault();
        if (!selectedCategory || !selectedSubCategory) {
            toast.error("Category and subcategory are required");
            return;
        }

        try {
            // Process the data to ensure arrays are properly formatted
            const processArrayField = (field) => {
                if (!field) return [];
                if (Array.isArray(field)) return field;
                if (typeof field === "string") {
                    return field
                        .split("\n")
                        .map((item) => item.trim())
                        .filter((item) => item);
                }
                return [];
            };

            // Process FAQ items
            const processFaq = (faq) => {
                if (!faq) return [];
                if (Array.isArray(faq)) return faq;
                if (typeof faq === "string") {
                    return faq
                        .split("\n\n")
                        .map((pair) => {
                            const [question, answer] = pair.split("\n");
                            return question && answer
                                ? { question, answer }
                                : null;
                        })
                        .filter((item) => item);
                }
                return [];
            };

            const categoryData = {
                ...newCategory,
                category: selectedCategory,
                subCategory: selectedSubCategory,
                description: newCategory.description || "",
                insights: processArrayField(newCategory.insights),
                features: processArrayField(newCategory.features),
                useCases: processArrayField(newCategory.useCases),
                advantages: processArrayField(newCategory.advantages),
                faq: processFaq(newCategory.faq),
                order: newCategory.order || 0,
            };

            await dispatch(updateCategory(categoryData));
            toast.success("Subcategory updated successfully");
            setIsUpdatingSubCategory(false);
            setNewCategory({
                category: "",
                subCategory: "",
                description: "",
                insights: "",
                features: "",
                useCases: "",
                advantages: "",
                faq: "",
                icon: "",
                order: 0,
            });
        } catch (error) {
            console.error("Error updating subcategory:", error);
            toast.error("Failed to update subcategory");
        }
    };

    const renderArrayField = (field, label) => {
        const items = Array.isArray(field) ? field : [];

        // Special handling for FAQ items
        if (label === "FAQ") {
            return (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {label}
                    </label>
                    {items.length > 0 ? (
                        <div className="space-y-4">
                            {items.map((item, index) => (
                                <div
                                    key={index}
                                    className="border-l-2 border-gray-200 pl-4"
                                >
                                    <p className="font-medium text-gray-800">
                                        {item.question}
                                    </p>
                                    <p className="text-gray-600 mt-1">
                                        {item.answer}
                                    </p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500 italic">
                            No {label.toLowerCase()} specified
                        </p>
                    )}
                </div>
            );
        }

        // Regular array items
        return (
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                </label>
                {items.length > 0 ? (
                    <ul className="list-disc list-inside space-y-1 text-gray-600">
                        {items.map((item, index) => (
                            <li key={index}>{item}</li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-gray-500 italic">
                        No {label.toLowerCase()} specified
                    </p>
                )}
            </div>
        );
    };

    const renderSubCategoryForm = (isViewing = false) => {
        return (
            <Card>
                <form
                    onSubmit={
                        isViewing
                            ? (e) => e.preventDefault()
                            : isUpdatingSubCategory
                            ? handleSubmitUpdate
                            : handleAddSubCategory
                    }
                    className="p-4 space-y-4"
                >
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold">
                            {isViewing
                                ? "View Subcategory"
                                : isUpdatingSubCategory
                                ? "Update Subcategory"
                                : "Add New Subcategory"}
                        </h3>
                        <button
                            type="button"
                            onClick={() => {
                                setIsViewingSubCategory(false);
                                setIsUpdatingSubCategory(false);
                                setIsAddingSubCategory(false);
                            }}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {!isViewing && (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Parent Category
                                </label>
                                <select
                                    value={selectedCategory || ""}
                                    onChange={(e) =>
                                        setSelectedCategory(e.target.value)
                                    }
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    required
                                    disabled={isUpdatingSubCategory}
                                >
                                    <option value="">Select a category</option>
                                    {Object.keys(categories).map((category) => (
                                        <option key={category} value={category}>
                                            {category}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {!isUpdatingSubCategory && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Subcategory Name
                                    </label>
                                    <input
                                        type="text"
                                        value={newCategory.subCategory}
                                        onChange={(e) =>
                                            setNewCategory({
                                                ...newCategory,
                                                subCategory: e.target.value,
                                            })
                                        }
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                            )}
                        </>
                    )}

                    {isViewing ? (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Description
                                </label>
                                <p className="text-gray-600 whitespace-pre-wrap">
                                    {subCategoryDetails?.description ||
                                        "No description provided"}
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Insights
                                </label>
                                <p className="text-gray-600 whitespace-pre-wrap">
                                    {Array.isArray(subCategoryDetails?.insights)
                                        ? subCategoryDetails.insights.join("\n")
                                        : subCategoryDetails?.insights ||
                                          "No insights provided"}
                                </p>
                            </div>

                            {renderArrayField(
                                subCategoryDetails?.features,
                                "Features"
                            )}
                            {renderArrayField(
                                subCategoryDetails?.useCases,
                                "Use Cases"
                            )}
                            {renderArrayField(
                                subCategoryDetails?.advantages,
                                "Advantages"
                            )}
                            {renderArrayField(subCategoryDetails?.faq, "FAQ")}
                        </>
                    ) : (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Description
                                </label>
                                <textarea
                                    value={newCategory.description}
                                    onChange={(e) =>
                                        setNewCategory({
                                            ...newCategory,
                                            description: e.target.value,
                                        })
                                    }
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    rows={3}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Insights
                                </label>
                                <textarea
                                    value={newCategory.insights}
                                    onChange={(e) =>
                                        setNewCategory({
                                            ...newCategory,
                                            insights: e.target.value,
                                        })
                                    }
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    rows={3}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Features (one per line)
                                </label>
                                <textarea
                                    value={
                                        Array.isArray(newCategory.features)
                                            ? newCategory.features.join("\n")
                                            : newCategory.features
                                    }
                                    onChange={(e) =>
                                        setNewCategory({
                                            ...newCategory,
                                            features: e.target.value,
                                        })
                                    }
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    rows={3}
                                    placeholder="Enter each feature on a new line"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Use Cases (one per line)
                                </label>
                                <textarea
                                    value={
                                        Array.isArray(newCategory.useCases)
                                            ? newCategory.useCases.join("\n")
                                            : newCategory.useCases
                                    }
                                    onChange={(e) =>
                                        setNewCategory({
                                            ...newCategory,
                                            useCases: e.target.value,
                                        })
                                    }
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    rows={3}
                                    placeholder="Enter each use case on a new line"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Advantages (one per line)
                                </label>
                                <textarea
                                    value={
                                        Array.isArray(newCategory.advantages)
                                            ? newCategory.advantages.join("\n")
                                            : newCategory.advantages
                                    }
                                    onChange={(e) =>
                                        setNewCategory({
                                            ...newCategory,
                                            advantages: e.target.value,
                                        })
                                    }
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    rows={3}
                                    placeholder="Enter each advantage on a new line"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    FAQ (one per line)
                                </label>
                                <textarea
                                    value={
                                        Array.isArray(newCategory.faq)
                                            ? newCategory.faq.join("\n")
                                            : newCategory.faq
                                    }
                                    onChange={(e) =>
                                        setNewCategory({
                                            ...newCategory,
                                            faq: e.target.value,
                                        })
                                    }
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    rows={3}
                                    placeholder="Enter each FAQ item on a new line"
                                />
                            </div>
                        </>
                    )}

                    {!isViewing && (
                        <div className="flex justify-end space-x-2">
                            <button
                                type="button"
                                onClick={() => {
                                    setIsViewingSubCategory(false);
                                    setIsUpdatingSubCategory(false);
                                    setIsAddingSubCategory(false);
                                }}
                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={`px-4 py-2 text-white rounded ${
                                    isUpdatingSubCategory
                                        ? "bg-blue-600 hover:bg-blue-700"
                                        : "bg-green-600 hover:bg-green-700"
                                }`}
                            >
                                {isUpdatingSubCategory ? "Update" : "Add"}{" "}
                                Subcategory
                            </button>
                        </div>
                    )}
                </form>
            </Card>
        );
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Category Management</h2>
                <div className="space-x-2">
                    <button
                        onClick={() => setIsAddingCategory(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        Add Category
                    </button>
                    <button
                        onClick={() => setIsAddingSubCategory(true)}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                        Add Subcategory
                    </button>
                </div>
            </div>

            {/* Add Category Form */}
            {isAddingCategory && (
                <Card>
                    <form
                        onSubmit={handleAddCategory}
                        className="p-4 space-y-4"
                    >
                        <h3 className="text-lg font-semibold">
                            Add New Category
                        </h3>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                Category Name
                            </label>
                            <input
                                type="text"
                                value={newCategory.category}
                                onChange={(e) =>
                                    setNewCategory({
                                        ...newCategory,
                                        category: e.target.value,
                                    })
                                }
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                Description
                            </label>
                            <textarea
                                value={newCategory.description}
                                onChange={(e) =>
                                    setNewCategory({
                                        ...newCategory,
                                        description: e.target.value,
                                    })
                                }
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                Icon (Font Awesome class)
                            </label>
                            <input
                                type="text"
                                value={newCategory.icon}
                                onChange={(e) =>
                                    setNewCategory({
                                        ...newCategory,
                                        icon: e.target.value,
                                    })
                                }
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                Order
                            </label>
                            <input
                                type="number"
                                value={newCategory.order}
                                onChange={(e) =>
                                    setNewCategory({
                                        ...newCategory,
                                        order: parseInt(e.target.value),
                                    })
                                }
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                        </div>
                        <div className="flex justify-end space-x-2">
                            <button
                                type="button"
                                onClick={() => setIsAddingCategory(false)}
                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                Add Category
                            </button>
                        </div>
                    </form>
                </Card>
            )}

            {/* Add/View/Update Subcategory Form */}
            {(isAddingSubCategory ||
                isViewingSubCategory ||
                isUpdatingSubCategory) &&
                renderSubCategoryForm(isViewingSubCategory)}

            {/* Categories List */}
            <Card>
                <div className="p-4">
                    <h3 className="text-lg font-semibold mb-4">Categories</h3>
                    <div className="space-y-4">
                        {Object.entries(categories).map(
                            ([categoryName, category]) => (
                                <div
                                    key={categoryName}
                                    className="border rounded-lg p-4"
                                >
                                    <div className="flex items-center space-x-2 mb-2">
                                        {category.icon && (
                                            <i
                                                className={`${category.icon} text-gray-500`}
                                            ></i>
                                        )}
                                        <h4 className="text-lg font-medium">
                                            {categoryName}
                                        </h4>
                                        <span className="text-sm text-gray-500">
                                            ({category.toolCount} tools)
                                        </span>
                                    </div>
                                    {category.description && (
                                        <p className="text-gray-600 mb-2">
                                            {category.description}
                                        </p>
                                    )}
                                    {category.subCategories &&
                                        category.subCategories.length > 0 && (
                                            <div className="ml-4 mt-2 space-y-2">
                                                {category.subCategories
                                                    .sort(
                                                        (a, b) =>
                                                            (a.order || 0) -
                                                            (b.order || 0)
                                                    )
                                                    .map((subCategory) => (
                                                        <div
                                                            key={
                                                                subCategory.uuid
                                                            }
                                                            className="flex justify-between items-center py-1"
                                                        >
                                                            <div className="flex items-center space-x-2">
                                                                <span className="text-gray-700">
                                                                    {
                                                                        subCategory.name
                                                                    }
                                                                </span>
                                                                <span className="text-sm text-gray-500">
                                                                    (
                                                                    {
                                                                        subCategory.count
                                                                    }{" "}
                                                                    tools)
                                                                </span>
                                                            </div>
                                                            <div className="flex space-x-2">
                                                                <button
                                                                    onClick={() =>
                                                                        handleViewSubCategory(
                                                                            categoryName,
                                                                            subCategory.name
                                                                        )
                                                                    }
                                                                    className="text-blue-600 hover:text-blue-800"
                                                                    title="View Details"
                                                                >
                                                                    <Eye className="h-4 w-4" />
                                                                </button>
                                                                <button
                                                                    onClick={() =>
                                                                        handleEditSubCategory(
                                                                            categoryName,
                                                                            subCategory.name
                                                                        )
                                                                    }
                                                                    className="text-blue-600 hover:text-blue-800"
                                                                    title="Edit"
                                                                >
                                                                    <Edit2 className="h-4 w-4" />
                                                                </button>
                                                                <button
                                                                    onClick={() =>
                                                                        handleDeleteSubCategory(
                                                                            categoryName,
                                                                            subCategory.name
                                                                        )
                                                                    }
                                                                    className="text-red-600 hover:text-red-800"
                                                                    title="Delete"
                                                                >
                                                                    <X className="h-4 w-4" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                            </div>
                                        )}
                                </div>
                            )
                        )}
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default CategoryManagement;
